import { Component, OnDestroy, OnInit } from '@angular/core';
import { RootMixin, UserAwareMixin, WithMenubarMixin } from '@fapp/mixins';
import { combineLatest, Subject, takeUntil } from 'rxjs';

import { PermissionsService } from '../../../core/services/permissions.service';
import {
  MenuItem,
  SidebarColorConfig,
} from '../../partials/sidebar/sidebar.component';

@Component({
  selector: 'app-user-layout',
  templateUrl: './user-layout.component.html',
  styleUrl: './user-layout.component.scss',
  standalone: false,
})
export class UserLayoutComponent
  extends WithMenubarMixin(UserAwareMixin(RootMixin))
  implements OnInit, OnDestroy
{
  sidebarCollapsed = false;
  filteredMenuItems: MenuItem[] = [];
  private destroy$ = new Subject<void>();

  // Custom color configuration using primary theme for managers
  sidebarColors: SidebarColorConfig = {
    background: 'var(--m4p-primary-50)',
    submenuBackground: 'var(--m4p-primary-100)',
    textColor: 'var(--m4p-secondary-700)',
    textHoverColor: 'var(--m4p-secondary-800)',
    activeTextColor: 'var(--m4p-primary-700)',
    hoverBackground: 'var(--m4p-primary-100)',
    activeBackground: 'var(--m4p-primary-200)',
    submenuActiveBackground: 'var(--m4p-primary-300)',
    borderColor: 'var(--m4p-primary-200)',
    activeBorderColor: 'var(--m4p-primary-500)',
    toggleColor: 'var(--m4p-secondary-600)',
    toggleHoverBackground: 'var(--m4p-primary-100)',
  };

  // Static menu items as fallback
  allMenuItems: MenuItem[] = [
    {
      label: 'Dashboard',
      icon: 'mat::dashboard',
      route: '/dashboard',
      requiredGroups: ['Manager', 'CompanyAdmin'], // Managers and CompanyAdmin can see dashboard
    },
    {
      label: 'Stock Counts',
      icon: 'mat::fact_check',
      route: '/stock-counts',
      requiredGroups: ['Manager', 'CompanyAdmin'], // Managers and CompanyAdmin can manage stock counts
      children: [
        {
          label: 'New Stock Count',
          icon: 'mat::add_circle',
          route: '/stock-counts/new',
        },
        {
          label: 'All Stock Counts',
          icon: 'mat::list',
          route: '/stock-counts',
        },
      ],
    },
    {
      label: 'AdHoc Scans',
      icon: 'mat::list_alt_add',
      route: '/adhoc-scans',
      requiredGroups: ['Manager', 'CompanyAdmin'], // Managers and CompanyAdmin can view AdHoc scans
      children: [
        {
          label: 'All AdHoc Scans',
          icon: 'mat::list',
          route: '/adhoc-scans',
          requiredGroups: ['Manager', 'CompanyAdmin'],
        },
      ],
    },
    {
      label: 'Mobile Devices',
      icon: 'mat::smartphone',
      route: '/mobile-devices',
      requiredGroups: ['Manager', 'CompanyAdmin'], // Managers and CompanyAdmin can manage mobile devices
      children: [
        {
          label: 'List Devices',
          icon: 'mat::list',
          route: '/mobile-devices',
        },
        {
          label: 'QR Code Generator',
          icon: 'mat::add_circle',
          route: '/mobile-devices/add',
        },
      ],
    },
    {
      label: 'Users',
      icon: 'mat::groups',
      route: '/users',
      requiredGroups: ['Manager', 'CompanyAdmin'], // Managers and CompanyAdmin can manage users
      children: [
        {
          label: 'All Users',
          icon: 'mat::list',
          route: '/users',
        },
        {
          label: 'Add User',
          icon: 'mat::add_circle',
          route: '/users/add',
        },
        {
          label: 'Company',
          icon: 'mat::business',
          route: '/company-settings',
        },
      ],
    },
    {
      label: 'My Profile',
      icon: 'mat::account_box',
      route: '/users/profile',
    },
  ];

  // Flag to determine whether to use dynamic or static menu
  private useDynamicMenu = true;

  constructor(private permissionsService: PermissionsService) {
    super();
  }

  ngOnInit(): void {
    if (this.useDynamicMenu) {
      // Load dynamic menu from backend
      combineLatest([
        this.currentUser$,
        this.permissionsService.loadSidebarMenu(),
      ])
        .pipe(takeUntil(this.destroy$))
        .subscribe(([, dynamicMenu]) => {
          console.log('UserLayoutComponent - Using dynamic menu:', dynamicMenu);

          // Use dynamic menu if available, no fallback to static menu
          if (dynamicMenu && dynamicMenu.length > 0) {
            this.filteredMenuItems = dynamicMenu;
          } else {
            console.log('UserLayoutComponent - No menu items from backend');
            // No fallback - if no groups/permissions, show nothing
            this.filteredMenuItems = [];
          }
        });
    } else {
      // Use static menu with client-side filtering (original behavior)
      this.currentUser$.pipe(takeUntil(this.destroy$)).subscribe(user => {
        console.log('UserLayoutComponent - Current user data:', user);
        console.log('UserLayoutComponent - User groups:', user?.groups);

        this.filteredMenuItems = this.filterMenuItemsByUserGroups(
          this.allMenuItems,
          user
        );
        console.log(
          'UserLayoutComponent - Filtered menu items:',
          this.filteredMenuItems
        );
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private filterMenuItemsByUserGroups(
    menuItems: MenuItem[],
    user: any
  ): MenuItem[] {
    if (!user) {
      return menuItems.filter(item => !item.requiredGroups);
    }

    return menuItems
      .filter(item => {
        // If no required groups, show the item
        if (!item.requiredGroups || item.requiredGroups.length === 0) {
          return true;
        }

        // Check if user has any of the required groups
        const userGroupNames =
          user.groups?.map((group: any) => group.name) || [];
        return item.requiredGroups.some(requiredGroup =>
          userGroupNames.includes(requiredGroup)
        );
      })
      .map(item => ({
        ...item,
        // Also filter children if they exist
        children: item.children
          ? this.filterMenuItemsByUserGroups(item.children, user)
          : item.children,
      }));
  }

  // Getter for template to use the filtered menu items
  get menuItems(): MenuItem[] {
    return this.filteredMenuItems;
  }
}
